import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { MapPin, X, Search, AlertCircle, Hash, Home, Loader } from 'lucide-react';
import { API_URL } from '../config/api';
import { useToast } from '../context/ToastContext';
import './AddressSearchInput.css';

const HighlightMatch = ({ text, query }) => {
    if (!query || !query.trim()) return <span>{text}</span>;
    const escaped = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const regex = new RegExp(`(${escaped})`, 'gi');
    const parts = text.split(regex);
    return (
        <span>
            {parts.map((part, i) =>
                regex.test(part) ? <strong key={i}>{part}</strong> : <span key={i}>{part}</span>
            )}
        </span>
    );
};

const AddressSearchInput = ({
    placeholder = 'Address, City, Zip...',
    variant = 'bar',
    displayValue = '',
    onSelect,
    onClear,
    showAddresses = true,
}) => {
    const [query, setQuery] = useState(displayValue);
    const [suggestions, setSuggestions] = useState(null);
    const [activeCities, setActiveCities] = useState(null);
    const [showDropdown, setShowDropdown] = useState(false);
    const [error, setError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [lookupLoading, setLookupLoading] = useState(false);
    const containerRef = useRef(null);
    const debounceRef = useRef(null);
    const navigate = useNavigate();
    const { showToast } = useToast();

    // Sync displayValue from parent
    useEffect(() => {
        setQuery(displayValue);
    }, [displayValue]);

    // Close on outside click
    useEffect(() => {
        const handleClickOutside = (e) => {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setShowDropdown(false);
                setError(null);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const fetchSuggestions = useCallback(async (q) => {
        if (q.length < 2) {
            setSuggestions(null);
            return;
        }
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/properties/search-suggestions?q=${encodeURIComponent(q)}`);
            if (!res.ok) throw new Error('Failed to fetch');
            const data = await res.json();
            setSuggestions(data);
            setError(null);
        } catch {
            setSuggestions(null);
        } finally {
            setLoading(false);
        }
    }, []);

    const fetchActiveCities = useCallback(async () => {
        if (activeCities) return; // already cached
        try {
            const res = await fetch(`${API_URL}/locations/active`);
            if (!res.ok) throw new Error('Failed to fetch');
            const data = await res.json();
            setActiveCities(data);
        } catch {
            setActiveCities([]);
        }
    }, [activeCities]);

    const handleInputChange = (e) => {
        const val = e.target.value;
        setQuery(val);
        setError(null);
        setShowDropdown(true);

        if (debounceRef.current) clearTimeout(debounceRef.current);
        if (val.length < 2) {
            setSuggestions(null);
            fetchActiveCities();
        } else {
            debounceRef.current = setTimeout(() => fetchSuggestions(val), 300);
        }
    };

    const handleClear = () => {
        setQuery('');
        setSuggestions(null);
        setError(null);
        setShowDropdown(false);
        onClear?.();
    };

    const getFirstSuggestion = () => {
        if (!suggestions) return null;
        if (suggestions.cities?.length > 0) return { type: 'city', ...suggestions.cities[0] };
        if (suggestions.zips?.length > 0) return { type: 'zip', ...suggestions.zips[0] };
        if (showAddresses && suggestions.addresses?.length > 0) return { type: 'address', ...suggestions.addresses[0] };
        return null;
    };

    const handleSelectCity = (item) => {
        const display = `${item.city}, ${item.state}`;
        setQuery(display);
        setShowDropdown(false);
        setError(null);
        onSelect?.({ type: 'city', city: item.city, state: item.state });
    };

    const handleSelectZip = (item) => {
        const display = `${item.zip_code}, ${item.city}, ${item.state}`;
        setQuery(display);
        setShowDropdown(false);
        setError(null);
        onSelect?.({ type: 'zip', zip_code: item.zip_code, city: item.city, state: item.state });
    };

    const handleSelectAddress = async (item) => {
        // Nominatim results have display_name + structured fields — call lookup endpoint
        const displayName = item.display_name;
        setQuery(displayName);
        setShowDropdown(false);
        setError(null);
        setLookupLoading(true);

        try {
            const res = await fetch(`${API_URL}/properties/lookup`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    address: displayName,
                    house_number: item.house_number || '',
                    road: item.road || '',
                    city: item.city || '',
                    state: item.state || '',
                    postcode: item.postcode || '',
                }),
            });
            if (!res.ok) {
                const errData = await res.json().catch(() => ({}));
                throw new Error(errData.detail || 'Property not found');
            }
            const data = await res.json();
            navigate(`/properties/${data.property_id}`);
        } catch (err) {
            showToast(err.message || 'Could not find this property', 'error');
        } finally {
            setLookupLoading(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            e.preventDefault();
            const first = getFirstSuggestion();
            if (first) {
                if (first.type === 'city') handleSelectCity(first);
                else if (first.type === 'zip') handleSelectZip(first);
                else if (first.type === 'address') handleSelectAddress(first);
            } else if (query.length >= 2) {
                setError(query);
                setShowDropdown(true);
            }
        }
        if (e.key === 'Escape') {
            setShowDropdown(false);
        }
    };

    const hasSuggestions = suggestions && (
        suggestions.cities?.length > 0 ||
        suggestions.zips?.length > 0 ||
        (showAddresses && suggestions.addresses?.length > 0)
    );

    const hasActiveCitiesContent = query.length < 2 && activeCities?.length > 0;
    const showDropdownContent = showDropdown && (hasSuggestions || hasActiveCitiesContent || error || loading);

    return (
        <div className={`address-search address-search-${variant}`} ref={containerRef}>
            <div className="address-search-input-wrapper">
                <input
                    type="text"
                    className={`address-search-input ${showDropdownContent ? 'dropdown-open' : ''}`}
                    placeholder={placeholder}
                    value={query}
                    onChange={handleInputChange}
                    onFocus={() => {
                        if (query.length >= 2) {
                            setShowDropdown(true);
                        } else {
                            fetchActiveCities();
                            setShowDropdown(true);
                        }
                    }}
                    onKeyDown={handleKeyDown}
                    disabled={lookupLoading}
                />
                {query && !lookupLoading && (
                    <button
                        type="button"
                        className="address-search-clear"
                        onClick={handleClear}
                        aria-label="Clear search"
                    >
                        <X size={variant === 'hero' ? 18 : 14} />
                    </button>
                )}
                <button
                    type="button"
                    className="address-search-submit"
                    onClick={() => {
                        const first = getFirstSuggestion();
                        if (first) {
                            if (first.type === 'city') handleSelectCity(first);
                            else if (first.type === 'zip') handleSelectZip(first);
                            else if (first.type === 'address') handleSelectAddress(first);
                        } else if (query.length >= 2) {
                            setError(query);
                            setShowDropdown(true);
                        }
                    }}
                    aria-label="Search"
                    disabled={lookupLoading}
                >
                    <Search size={variant === 'hero' ? 22 : 16} />
                </button>
            </div>

            {(showDropdownContent || lookupLoading) && (
                <div className="address-search-dropdown">
                    {lookupLoading ? (
                        <div className="search-lookup-loading">
                            <Loader size={20} className="search-lookup-spinner" />
                            <div>
                                <span className="search-lookup-title">Looking up property...</span>
                                <span className="search-lookup-hint">This may take a few seconds</span>
                            </div>
                        </div>
                    ) : error ? (
                        <div className="search-error-box">
                            <AlertCircle size={18} className="search-error-icon" />
                            <div>
                                <span>Sorry, we couldn't find listings for: <strong>{error}</strong></span>
                                <p className="search-error-hint">Please check your spelling or try a different search.</p>
                            </div>
                        </div>
                    ) : (
                        <>
                            {hasActiveCitiesContent && (
                                <div className="search-group">
                                    <div className="search-group-label">Active Markets</div>
                                    {activeCities.map((loc, i) => (
                                        <div key={`active-${i}`} className="search-suggestion-item" onClick={() => handleSelectCity({ city: loc.city, state: loc.state_abbr })}>
                                            <MapPin size={16} className="suggestion-icon" />
                                            <span>{loc.city}, {loc.state_abbr}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                            {suggestions?.cities?.length > 0 && (
                                <div className="search-group">
                                    <div className="search-group-label">City</div>
                                    {suggestions.cities.map((item, i) => (
                                        <div key={`city-${i}`} className="search-suggestion-item" onClick={() => handleSelectCity(item)}>
                                            <MapPin size={16} className="suggestion-icon" />
                                            <HighlightMatch text={`${item.city}, ${item.state}`} query={query} />
                                        </div>
                                    ))}
                                </div>
                            )}
                            {suggestions?.zips?.length > 0 && (
                                <div className="search-group">
                                    <div className="search-group-label">Zip Code</div>
                                    {suggestions.zips.map((item, i) => (
                                        <div key={`zip-${i}`} className="search-suggestion-item" onClick={() => handleSelectZip(item)}>
                                            <Hash size={16} className="suggestion-icon" />
                                            <HighlightMatch text={`${item.zip_code}, ${item.city}, ${item.state}`} query={query} />
                                        </div>
                                    ))}
                                </div>
                            )}
                            {showAddresses && suggestions?.addresses?.length > 0 && (
                                <div className="search-group">
                                    <div className="search-group-label">Address</div>
                                    {suggestions.addresses.map((item, i) => (
                                        <div key={`addr-${i}`} className="search-suggestion-item" onClick={() => handleSelectAddress(item)}>
                                            <Home size={16} className="suggestion-icon" />
                                            <HighlightMatch text={item.display_name} query={query} />
                                        </div>
                                    ))}
                                </div>
                            )}
                            {loading && !hasSuggestions && (
                                <div className="search-loading">Searching...</div>
                            )}
                        </>
                    )}
                </div>
            )}
        </div>
    );
};

export default AddressSearchInput;
